/*
  Design proof viewer.

  A personalised line carries a link to the proof the customer built. Sent to
  a new tab it drops them out of the cart onto a bare CDN image; here it opens
  centred over the page they are already on.

  The dialog is built once, on the first click, and reused — there are up to
  three pieces of artwork per line and a cart can hold many lines, so putting
  one in the markup per link would be wasteful. Anything carrying
  data-proof-src is picked up, wherever it was rendered: the cart pages draw
  their links in Liquid (snippets/cart-line-properties.liquid) and the header
  drawer draws its own in script, and both reach this the same way.

  Brand colours arrive as --proof-* custom properties set per layout, so the
  same dialog reads as sports, workwear or tackle without knowing which side
  it is on. Styles live in assets/cart-line-properties.css.
*/
(function () {
  'use strict';

  /* Where mug-spin.js lives, handed over by the layout rather than loaded by
     it: it is a WebGL renderer that most visits never need, so it is fetched
     the first time somebody opens a mug's proof and never on a page where
     nobody does. Read at parse time, while currentScript still points here. */
  var MUG_SPIN_SRC = document.currentScript
    ? document.currentScript.getAttribute('data-mug-spin')
    : null;
  var mugSpinLoad = null;

  function loadMugSpin() {
    if (window.MugSpin) return Promise.resolve(true);
    if (!MUG_SPIN_SRC) return Promise.resolve(false);
    if (mugSpinLoad) return mugSpinLoad;

    mugSpinLoad = new Promise(function (resolve) {
      var el = document.createElement('script');
      el.src = MUG_SPIN_SRC;
      el.onload = function () { resolve(!!window.MugSpin); };
      el.onerror = function () { resolve(false); };
      document.head.appendChild(el);
    });
    return mugSpinLoad;
  }

  var dialog = null;
  var img = null;
  var title = null;
  var original = null;
  var loadingText = null;
  var mugCanvas = null;
  var mug = null;
  var wrapImg = null;

  /* The mug is rebuilt whenever the geometry changes — a customer can have an
     11oz and a 15oz in the same basket — but the renderer holds a WebGL
     context, and browsers cap how many a page may have. One is kept and
     re-meshed rather than one made per line. */
  var mugSpecJSON = null;

  function paintWrap(ctx, w, h) {
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    if (wrapImg && wrapImg.naturalWidth) ctx.drawImage(wrapImg, 0, 0, w, h);
  }

  /* The mug fills the plate at whatever size the dialog ended up, and WebGL
     needs the backing store in device pixels or the ceramic reads soft. */
  function sizeMug() {
    if (!mug || !mug.ok) return;
    var box = mugCanvas.parentElement.getBoundingClientRect();

    /* A stage with no box yet — the dialog still laying out, or a tab the
       browser has given no viewport — would pin the canvas at 1x1 and leave
       it there. Leave the last good size alone and wait for the next call. */
    if (box.width < 2 || box.height < 2) return;

    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = Math.max(1, Math.round(box.width * dpr));
    var h = Math.max(1, Math.round(box.height * dpr));
    if (mugCanvas.width !== w || mugCanvas.height !== h) {
      mugCanvas.width = w;
      mugCanvas.height = h;
    }
    mug.draw();
  }

  /* One class drives the plate: 'loading' holds the spinner, 'ready' swaps to
     the proof, 'failed' leaves the message and the full-size link. Set on the
     dialog rather than toggled per element so there is one state to read. */
  function settle(state) {
    dialog.classList.remove('is-loading', 'is-ready', 'is-failed');
    dialog.classList.add('is-' + state);
  }

  function build() {
    if (dialog) return dialog;

    dialog = document.createElement('dialog');
    dialog.className = 'proof-dialog';
    dialog.setAttribute('aria-labelledby', 'proof-dialog-title');
    dialog.innerHTML =
      '<div class="proof-dialog__head">'
      + '<p class="proof-dialog__title" id="proof-dialog-title"></p>'
      + '<button type="button" class="proof-dialog__close" aria-label="Close proof">'
      + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" '
      + 'stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>'
      + '</button>'
      + '</div>'
      + '<div class="proof-dialog__plate">'
      + '<div class="proof-dialog__loading" role="status">'
      + '<span class="proof-dialog__spinner" aria-hidden="true"></span>'
      + '<span class="proof-dialog__loading-text">Loading your proof</span>'
      + '</div>'
      + '<img class="proof-dialog__img" alt="">'
      + '<div class="proof-dialog__stage">'
      + '<canvas class="proof-dialog__mug"></canvas>'
      + '<p class="proof-dialog__hint">Drag to spin</p>'
      + '</div>'
      + '</div>'
      + '<div class="proof-dialog__foot">'
      + '<a class="proof-dialog__original" target="_blank" rel="noopener">Open the full-size file</a>'
      + '</div>';

    img = dialog.querySelector('.proof-dialog__img');
    title = dialog.querySelector('.proof-dialog__title');
    original = dialog.querySelector('.proof-dialog__original');
    loadingText = dialog.querySelector('.proof-dialog__loading-text');
    mugCanvas = dialog.querySelector('.proof-dialog__mug');

    img.addEventListener('load', function () { settle('ready'); });
    img.addEventListener('error', function () {
      loadingText.textContent = 'That proof could not be loaded.';
      settle('failed');
    });

    dialog.querySelector('.proof-dialog__close').addEventListener('click', function () {
      dialog.close();
    });

    /* A click that lands on the dialog element itself landed on the backdrop —
       the panel's own children cover every pixel inside it. */
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });

    document.body.appendChild(dialog);
    return dialog;
  }

  /* The flat proof: a picture of the mug at the angle it was applied. */
  function showFlat(src) {
    dialog.classList.remove('is-spinnable');
    /* Assigning the same src again fires no load event, so a proof reopened
       from cache would sit on the spinner forever. */
    if (img.getAttribute('src') !== src) img.src = src;
    if (img.complete && img.naturalWidth) settle('ready');
  }

  /*
    The real thing: the printed wrap on the mug it was made for, turnable.

    Everything here can fail on a customer's machine — WebGL refused, the CDN
    slow, the spec written by an older version of the designer — and none of
    those should cost them their proof. Any of them falls back to the flat
    image, which is what the dialog showed before this existed.
  */
  /* The 11oz the designer falls back to when a product names no mug of its
     own. Used only to stand in for a line that predates the _Mug property. */
  var DEFAULT_BODY = { body_mm_diameter: 82, body_mm_height: 95 };

  /*
    The mug's millimetres, from the line as it actually is.

    First choice is _Mug, written by the designer and exact. Failing that, the
    print spec — "200 x 85mm full wrap at 300dpi" — gives the wrap's true size,
    which is what the artwork is mapped by; only the body is assumed, and a
    15oz then reads slightly too slim. That is worth it: the alternative for
    every mug bought before 2 Sep 2026 is a flat picture, and the exact file is
    still one click away under the proof.

    The garment designer writes a print spec of the same shape — "210 x 297mm
    at 300dpi" — so the millimetres on their own do not mean a mug, and reading
    them as one put a shirt's artwork on a ceramic body. Only the mug designer
    says "full wrap". Without that phrase and without _Mug, this is not a mug.
  */
  function resolveSpec(specJSON, printFile) {
    if (specJSON) {
      try {
        var spec = JSON.parse(specJSON);
        if (spec && spec.body_mm_diameter && spec.wrap_mm_wide) return spec;
      } catch (e) { /* fall through to the print spec */ }
    }

    if (!printFile || !/full wrap/i.test(printFile)) return null;
    var mm = /(\d+(?:\.\d+)?)\s*[x\u00d7]\s*(\d+(?:\.\d+)?)\s*mm/i.exec(printFile);
    if (!mm) return null;

    return {
      body_mm_diameter: DEFAULT_BODY.body_mm_diameter,
      body_mm_height: DEFAULT_BODY.body_mm_height,
      wrap_mm_wide: parseFloat(mm[1]),
      wrap_mm_tall: parseFloat(mm[2])
    };
  }

  function showMug(wrapUrl, specJSON, flatSrc, printFile) {
    var spec = resolveSpec(specJSON, printFile);
    if (!spec) return showFlat(flatSrc);
    var specKey = JSON.stringify(spec);

    loadingText.textContent = 'Loading your mug';

    var image = new Image();
    /* The wrap becomes a WebGL texture, so it has to arrive CORS-clean or the
       upload throws a security error rather than merely looking wrong. */
    image.crossOrigin = 'anonymous';

    image.onload = function () {
      wrapImg = image;

      if (mug && mugSpecJSON !== specKey) {
        mug.setSpec(spec);
        mugSpecJSON = specKey;
      }

      if (!mug) {
        try {
          mug = new window.MugSpin(mugCanvas, { spec: spec, paint: paintWrap });
        } catch (e) { mug = null; }
        if (!mug || !mug.ok) { mug = null; return showFlat(flatSrc); }
        mugSpecJSON = specKey;
      }

      dialog.classList.add('is-spinnable');
      settle('ready');

      /* Once now — reading the box forces the layout the classes just changed,
         so this is enough on its own — and again on the next frame, for
         browsers that had not finished sizing the dialog. Not rAF alone: it
         does not fire in a background tab, and a customer who opens the cart
         in one and comes back to it would find an unsized canvas. */
      sizeMug();
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(sizeMug);
    };

    image.onerror = function () { showFlat(flatSrc); };
    image.src = wrapUrl;
  }

  window.addEventListener('resize', function () {
    if (dialog && dialog.open && dialog.classList.contains('is-spinnable')) sizeMug();
  });

  document.addEventListener('click', function (event) {
    var link = event.target.closest ? event.target.closest('[data-proof-src]') : null;
    if (!link) return;

    var src = link.getAttribute('data-proof-src');
    if (!src) return;

    /* No <dialog> support: let the link do what it says on the tin. */
    var panel = build();
    if (typeof panel.showModal !== 'function') return;

    event.preventDefault();

    var label = link.getAttribute('data-proof-title') || 'Design proof';
    title.textContent = label;
    img.alt = label;
    original.href = src;

    loadingText.textContent = 'Loading your proof';
    settle('loading');

    var wrap = link.getAttribute('data-proof-wrap');
    var spec = link.getAttribute('data-proof-mug');
    var printFile = link.getAttribute('data-proof-printfile');

    if (wrap) {
      /* Open on the spinner straight away and fetch the renderer behind it —
         the customer should never be waiting on a blank screen for a script
         they did not ask for. If it will not load, they get the flat proof. */
      panel.showModal();
      loadMugSpin().then(function (ready) {
        if (!dialog.open) return;
        if (ready) showMug(wrap, spec, src, printFile);
        else showFlat(src);
      });
      return;
    }

    showFlat(src);
    panel.showModal();
  });
}());
